const qs = require('qs');
import { AppState } from '../helpers/app-state.js';
import { showFatalError } from '../helpers/error-ui.js';

// Safely read and parse JSON from sessionStorage, returning null for missing or invalid values
function safeGetJSON(ss, key) {
	const raw = ss.getItem(key);
	if (raw == null || raw === 'undefined') return null;
	try {
		return JSON.parse(raw);
	} catch (err) {
		console.warn('Failed to parse sessionStorage key "' + key + '":', err);
		return null;
	}
}

const SessionStorageService = {
	_callbacks: {},
	_refreshInProgress: false,

	init(callbacks) {
		this._callbacks = callbacks;
	},

	readSessionStorage() {
		let ss;
		if (AppState.typoError) return;
		try {
			if (!navigator.cookieEnabled) throw '';
			ss = window.sessionStorage;
		} catch (err) {
			console.error(err);
			document.getElementById('loading').innerHTML = `
				<div class="p-2 center-align">
					<div class="mb-4"><img src="./../../resources/assets/fix_car.png" alt="Man fixing car" class="responsive-img px-12" style="width:500px; height:auto;"/></div>
					<h3 class="text-lg mt-4">Session storage blocked.</h3>
					<p class="text-secondary">Please set your browser to Allow all cookies, then try again. Thank you. (support@nthround.com)</p>
				</div>
			  `;
			throw 'Fatal error.';
		}

		userData = safeGetJSON(ss, 'user.Data');
		isAdmin = userData?.carlist?.includes('^' + client) || false;
		const tags = userData?.clients[client]?.tags || [];
		isCont = tags.includes('CONTRIBUTOR') || false;
		isSupv = tags.includes('SUPERVISOR') || false;
		isExec = tags.includes('EXECUTIVE') || false;
		VOA = tags.includes('VIEW ONLY ADMIN');

		clientData = safeGetJSON(ss, client + '.Data');
		shareholderData = safeGetJSON(ss, 'shareholder.Data');
		// Only read cap table if data exists in sessionStorage to avoid warnings
		if (NthUtil.sessionStore.hasCapTable(client)) {
			capTableData = NthUtil.sessionStore.readCapTable(client);
		} else {
			capTableData = [];
		}
		if (capTableData) {
			if (blurNames) {
				let blurNamesArray = blurNames.split('\r\n');
				for (let i = 0; i < capTableData.length; i++) capTableData[i].user_name = blurNamesArray[i];
				NthUtil.sessionStore.updateCapTable(client, capTableData);
			} else if (isSupv && !isExec) {
				// #6271: isExec always wins over isSupv scoping, matching generateCapTable below
				const scopeStatuses = (
					userData.clients[client].override_statuses?.length ? userData.clients[client].override_statuses : [userData.clients[client].status]
				).map((s) =>
					String(s || '')
						.toLowerCase()
						.trim()
				);

				capTableData = capTableData.filter((u) => scopeStatuses.includes(u.clients[client].status?.toLowerCase().trim()));
				NthUtil.sessionStore.updateCapTable(client, capTableData);
			}
		}
		this._callbacks.setLabels('read storage');
	},

	// Max 2 attempts total (1 retry) with a short delay in between. Both failure
	// modes below are commonly transient (a network blip, or a gateway/proxy
	// hiccup returning an HTML error page instead of JSON) — see
	// NTH-INVESTOR-PORTAL-D1 / NTH-INVESTOR-PORTAL-D4, GH #5994.
	_MAX_REFRESH_ATTEMPTS: 2,
	_REFRESH_RETRY_DELAY_MS: 1500,

	refreshSessionStorage(generate = true) {
		if (AppState.typoError) return;
		if (this._refreshInProgress) {
			console.log('refreshSessionStorage skipped - already in progress');
			return;
		}
		this.readSessionStorage();
		// don't get data if on signin, join, or forms
		if (AppState.pn.includes('/signin') || AppState.pn.includes('/join') || AppState.pn.includes('/forms')) return;
		AppState.refreshTime = Date.now();
		console.log('Starting refreshSessionStorage: ' + (Date.now() - AppState.refreshTime) + ' ms');

		let uid = authedUser;
		if (!uid) {
			console.log('No userid.  Signing out.');
			signOut();
			return;
		}

		// Native Cognito MFA challenge pending — no JWT exists yet, so this call would either
		// fail outright or be misread as "not logged in" and redirect to /signin/, abandoning
		// the challenge before the user has a chance to enter their code. checkAuth() (auth.js)
		// is already handling the challenge via this same nth.MfaPending signal; once it
		// completes, carryOnLoading() dispatches RefreshSessionStorage and this re-fires
		// naturally with a real cookie.
		if (!getCookie() && checkCookie('nth.MfaPending')) {
			console.log('refreshSessionStorage skipped - native MFA challenge pending');
			return;
		}

		this._refreshInProgress = true;
		this._fetchClientData(generate, uid, 1);
	},

	// Split out of refreshSessionStorage() so a transient failure (bad network,
	// or the API returning something that isn't a JSON array) can retry the same
	// request instead of immediately giving up. `attempt` is 1-based.
	_fetchClientData(generate, uid, attempt) {
		const self = this;

		// Retries once (after _REFRESH_RETRY_DELAY_MS) if attempts remain, otherwise
		// gives up and shows the fatal-error UI exactly as before.
		const retryOrFail = (message, context, extra) => {
			if (attempt < self._MAX_REFRESH_ATTEMPTS) {
				console.warn('[refreshSessionStorage] attempt ' + attempt + ' failed (' + context + '), retrying in ' + self._REFRESH_RETRY_DELAY_MS + 'ms');
				setTimeout(() => self._fetchClientData(generate, uid, attempt + 1), self._REFRESH_RETRY_DELAY_MS);
				return;
			}
			self._refreshInProgress = false;
			if (!AppState.typoError) showFatalError(message, context, extra);
		};

		// #6241: axios can still be loading (CDN blocked/slow) when this fires --
		// retry like any other transient failure instead of throwing ReferenceError.
		if (typeof axios === 'undefined') {
			retryOrFail(
				'Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)',
				'axios not loaded (CDN blocked or slow?) before refreshSessionStorage fetch',
				{ page: window.location.pathname, attempt }
			);
			return;
		}

		cookie = getCookie();
		console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - AppState.refreshTime) + ' ms (attempt ' + attempt + ')');
		axios.defaults.withCredentials = cookie ? false : true;
		const aliasUserId = sessionStorage.getItem('alias.userid');
		const previewUser = sessionStorage.getItem('preview') === 'true' ? safeGetJSON(sessionStorage, 'shareholder.Data') : null;
		const previewUserId = previewUser?.clients?.[client] ? previewUser.userid : null;
		const viewid = previewUserId || aliasUserId || null;

		axios
			.get(clientURL, {
				params: {
					client: client,
					userid: authedUser,
					cookie: cookie,
					host: window.location.hostname === 'localhost' ? client + '.nthround.io' : window.location.host,
					...(viewid ? { viewid } : {}),
				},
			})
			.then(function (response) {
				console.log('***Speed Test AFTER axios get : ' + (Date.now() - AppState.refreshTime) + ' ms');
				// Validate response is an array of objects (not a string error message/HTML page)
				if (!Array.isArray(response.data)) {
					retryOrFail(
						'Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)',
						'API returned non-array response in refreshSessionStorage',
						{ responseType: typeof response.data, page: window.location.pathname, attempt }
					);
					return;
				}
				// If the user is not logged in build the public view
				if (!response.data[0]) {
					if (!AppState.pn.includes('/signin') && !AppState.pn.includes('/join') && !AppState.pn.includes('/forms'))
						window.location.replace('/signin/');
				} else if (!uid || !response.data[0]['user']) {
					console.log('refreshSessionStorage no user: ' + (Date.now() - AppState.refreshTime) + ' ms');
					clientData = response.data[0];
					self._callbacks.setLabels('no-user refresh');
					sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
					document.dispatchEvent(new Event('StorageComplete'));
					if (uid.endsWith('@nthround.com')) {
						let userlistURL = addminURL + '?client=' + client + '&userid=' + authedUser + '&cookie=' + cookie + '&userlist=' + uid + '&admin=True';
						axios
							.post(userlistURL)
							.then(function (res) {
								console.log(res);
								location.reload();
							})
							.catch(function (err) {
								console.log(err);
							});
					}
				} else {
					if (!response.data[0]['user']) alert('Missing user data: responseData[0] = ', response.data[0]);

					userData = response.data[0]['user'];
					isAdmin = userData?.carlist?.includes('^' + client) || false;
					const tags = userData?.clients[client]?.tags || [];
					isCont = tags.includes('CONTRIBUTOR') || false;
					isSupv = tags.includes('SUPERVISOR') || false;
					isExec = tags.includes('EXECUTIVE') || false;
					VOA = tags.includes('VIEW ONLY ADMIN');

					let showTreemap = false;
					if (userData.clients[client] && userData.clients[client].hasOwnProperty('showTreemap')) showTreemap = userData.clients[client].showTreemap;
					sessionStorage.setItem('showTreemap', showTreemap);
					sessionStorage.removeItem('alias.Data');

					// CONSOLIDATED on 1-7-2022
					if (!response.data[1]['client']) alert('Missing client data responseData[1] = ', response.data[1]);
					clientData = response.data[1]['client'];
					self._callbacks.setLabels('refresh');

					// Detect spurious data
					if (EXTERNAL_DATA) {
						let foundTrans = false;
						AppState.classes.forEach((c) => {
							if (clientData.equity[c].transactions?.length) foundTrans = true;
						});
						if (foundTrans) sendAlert('Unexpected Transactions (' + client + '.nthround.io)', JSON.stringify(clientData.equity).slice(0, 50));
					}

					// AUTOPOPULATE any userlisted user that is missing client data (get this with brand new account)
					if (!userData['clients'][client] && !userData.userid.toLowerCase().includes('nthround.com'))
						alert("Missing user client data userData['clients'] = ", userData['clients']);
					let userClient = userData['clients'][client];
					if (!userClient || !userClient['shares']) {
						userData = self._callbacks.autoPopulateUser(userData.userid);
					}

					if (STD_EXT_DATA) self._callbacks.getStandardTransactionsFromFile('userData', null, false, false, false, '');
					else if (isAlera) self._callbacks.getAleraHoldingsFromFile('userData', null, false, false, false, '');

					sessionStorage.setItem('user.Data', JSON.stringify(userData));
					sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));

					console.log('Client/User complete: ' + (Date.now() - AppState.refreshTime) + ' ms');
					if (!AppState.pn.includes('/admin') && !AppState.pn.includes('/supervisor') && !EXTERNAL_DATA) {
						document.dispatchEvent(new Event('StorageComplete'));
						let goToAdmin = document.getElementById('goToAdmin');
						if (goToAdmin) {
							goToAdmin.style.pointerEvents = 'none';
							goToAdmin.style.backgroundColor = stdColor[1];
							goToAdmin.innerHTML = 'Go to Admin (loading...)';
						}
					}

					self.refreshCapTable(generate); //gets table from s3
				}
				self._refreshInProgress = false;
			})
			.catch(function (err) {
				retryOrFail(
					'Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)',
					'refreshSessionStorage network/API error',
					{ error: String(err), page: window.location.pathname, attempt }
				);
			});
	},

	async refreshCapTable(generate = true) {
		/* Retrieves cap table as indicated by the generate parameter
			param generate:
				If true, generates cap table traditionally
				If false, retrieves the latest cap table from s3
		*/
		const self = this;

		// MAGIC REFRESH (investor-agreement) for Nth Rounders
		const _isPageReload = (performance.getEntriesByType('navigation')[0] || {}).type === 'reload';
		if (_isPageReload && window.location.href.includes('/investor-agreement/') && authedUser.includes('@nthround.com')) {
			let userClientData = {
				investor: {
					complete: false,
					entity: false,
					income: false,
					netWorth: false,
					knowledge: false,
				},
				joinder: {
					complete: false,
				},
				operatingAgreement: {
					complete: false,
				},
			};
			axios
				.post(
					postURL,
					JSON.stringify({
						eventName: 'index.cap_table_refreshed',
						userid: authedUser,
						client: client,
						cookie: cookie,
						clients: {
							[client]: userClientData,
						},
					})
				)
				.then(function (res) {
					console.log(res);
				})
				.catch(function (err) {
					console.error(err);
				});

			userData.clients[client].investor = userClientData.investor;
			userData.clients[client].joinder = userClientData.joinder;
			userData.clients[client].operatingAgreement = userClientData.operatingAgreement;
		}

		const previewingShareholder = !!(sessionStorage.getItem('preview') === 'true' && safeGetJSON(sessionStorage, 'shareholder.Data')?.clients?.[client]);

		// if just a user, bail out
		if (!generate && !AppState.pn.startsWith('/admin') && !AppState.pn.startsWith('/supervisor')) {
			document.dispatchEvent(new Event('CapTableReady'));
			if (!isAdmin || previewingShareholder) return;
		}

		// Non-admin users should never receive the full cap table, but if generate=true
		// (e.g. after a proxy vote), fire-and-forget the API call so the backend creates
		// a fresh snapshot for admins — just discard the response.
		if ((!isAdmin || previewingShareholder) && !isSupv && !isExec && !AppState.pn.startsWith('/admin') && !AppState.pn.startsWith('/supervisor')) {
			if (generate && !previewingShareholder) {
				axios
					.get(shareholderURL, {
						params: {
							userid: authedUser,
							client: client,
							cookie: cookie,
							host: window.location.hostname === 'localhost' ? client + '.nthround.io' : window.location.host,
						},
						paramsSerializer: function (params) {
							return qs.stringify(params, { arrayFormat: 'repeat' });
						},
					})
					.catch(() => {});
			}
			document.dispatchEvent(new Event('CapTableReady'));
			return;
		}

		console.log('Starting Refresh CapTable: ' + (Date.now() - AppState.refreshTime) + ' ms');

		// Get cap table data
		if (true) {
			console.log('Getting ShareholderData: ' + (Date.now() - AppState.refreshTime) + ' ms');
			let capTableName = clientData.captable_data;

			if (!generate && capTableName) {
				/** Gets cap table image from S3 **/
				console.log('### Getting Cap Table Image from S3 ###');
				let captableURL = await window.getSnapshotUrl(capTableName);
				fetch(captableURL)
					.then(async function (response) {
						if (response.status === 403) return await generateCapTableLegacy();
						return response.text();
					})
					.then(function (txt) {
						let res = JSON.parse(txt);
						console.log(
							'### Got S3 ShareholderData (from ' +
								capTableName +
								' image, created ' +
								moment.unix(capTableName.substring(0, 10)).fromNow() +
								'): ' +
								(Date.now() - AppState.refreshTime) +
								' ms'
						);
						generateCapTable(res);
					})
					.catch((error) => {
						console.log(error);
					});
			} else {
				generateCapTableLegacy();
			}
			async function generateCapTableLegacy() {
				/** Our legacy/old way of generating captables **/
				console.log('### GENERATING CAP TABLE IMAGE ###');
				console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - AppState.refreshTime) + ' ms');
				await axios
					.get(shareholderURL, {
						params: {
							userid: authedUser,
							client: client,
							cookie: cookie,
							host: window.location.hostname === 'localhost' ? client + '.nthround.io' : window.location.host,
						},
						paramsSerializer: function (params) {
							return qs.stringify(params, { arrayFormat: 'repeat' });
						},
					})
					.then(async (res) => {
						if (typeof res.data === 'object') {
							/** handles image response **/
							console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - AppState.refreshTime) + ' ms');
							let contentLength = Number(res.headers['content-length']) / 1024;
							console.log('### Got NEW ShareholderData (' + contentLength.toFixed(0) + ' KB): ' + (Date.now() - AppState.refreshTime) + ' ms');
							if (contentLength > 5200) {
								let subj = 'Lambda Payload Size Alert (' + client + '.nthround.io)';
								let msg =
									'Message length = ' +
									contentLength.toFixed(0) +
									' KB.<br><br>Consider adding ' +
									client +
									' to LARGE_CLIENTS array in returnShareholderData lambda function and to shouldCompressClient() in auth.js.';
								sendAlert(subj, msg);
								console.log(msg);
							}
							generateCapTable(res);
						} else if (typeof res.data === 'string') {
							/** handles url response **/
							console.log('### Getting Cap Table Image from URL ###');
							let captableURL = res.data;
							// If backend returned an unsigned CloudFront URL, get a signed one
							if (captableURL.includes('d3joenepwjic7e.cloudfront.net/')) {
								const docName = captableURL.split('/').pop();
								captableURL = await window.getSnapshotUrl(docName);
							}
							await fetch(captableURL)
								.then(function (response) {
									if (response.status === 403) {
										console.error('Cap table URL returned 403 (presigned URL may be invalid):', captableURL);
										return null;
									}
									return response.text();
								})
								.then(function (txt) {
									if (!txt) return;
									let res = JSON.parse(txt);
									console.log('### Got URL ShareholderData (from ' + captableURL + '): ' + (Date.now() - AppState.refreshTime) + ' ms');
									generateCapTable(res);
								});
						}
					})
					.catch(function (error) {
						console.error(error);
					});
			}

			function generateCapTable(res) {
				// #6271: filter to effective status scope for isSUPV only -- Executives see the
				// full cap table by design, matching the isSupv && !isExec path in index.js.
				if (isSupv && !isExec && !isAlera) {
					const scopeStatuses = (
						userData.clients[client].override_statuses?.length ? userData.clients[client].override_statuses : [userData.clients[client].status]
					).map((s) =>
						String(s || '')
							.toLowerCase()
							.trim()
					);

					res = {
						data: res.data.filter((user) => scopeStatuses.includes(user.Item?.clients[client].status?.toLowerCase().trim())),
					};
				}

				if (isAlera) self._callbacks.getAleraHoldingsFromFile('capTable', res, false, false, false, '');
				else {
					console.log('CapTable BEFORE complete: ' + (Date.now() - AppState.refreshTime) + ' ms');
					capTableData = [];
					res.data.forEach(function (elem) {
						// not sure this is the behavior we want for a brand new client with little or no data
						if (!elem['Item'] || !elem['Item']['clients']) return;

						// AUTOPOPULATE any userlisted user that is missing client data
						let thisUserData = elem['Item'];
						let userClient = thisUserData.clients[client];
						if (!userClient || !userClient['shares']) {
							thisUserData = self._callbacks.autoPopulateUser(thisUserData.userid);
							elem['Item'] = thisUserData;
						}

						// only push populated users
						if (!elem['Item'].clients[client].shares) {
							console.log('Bad client data:');
							console.log(elem);
						} else {
							capTableData.push(elem['Item']);
						}
					});
					capTableData = removeNthAdmins(capTableData);
					if (blurNames) {
						let blurNamesArray = blurNames.split('\r\n');
						for (let i = 0; i < capTableData.length; i++) capTableData[i].user_name = blurNamesArray[i];
					}
					NthUtil.sessionStore.updateCapTable(client, capTableData);
					console.log('CapTable AFTER complete: ' + (Date.now() - AppState.refreshTime) + ' ms');
					document.dispatchEvent(new Event('CapTableReady'));
					self._callbacks.sessionStorageSize();
					let goToAdmin = document.getElementById('goToAdmin');
					if (goToAdmin) {
						goToAdmin.style.pointerEvents = null;
						goToAdmin.style.backgroundColor = null;
						goToAdmin.innerHTML = AppState.pn.includes('admin') ? 'View Investor Portal' : 'Go to Admin';
					}
				}
			}
		}
	},
};

export { SessionStorageService };
